// Session- + matter-scoped completion watcher for discovery intakes
// (plan: dapper-dreaming-sifakis.md F6 — the async payoff). When a paralegal
// drops a file and walks away from the extraction page, this provider (mounted
// under CaseShell, wrapping every /cases/:caseKey/* route) keeps polling the
// All Files endpoint for the intakes they started THIS session and fires a
// non-blocking Toast on an actionable transition:
//
//   ready_for_review → "{label} finished extracting · Review"  (→ /intake/:id)
//   failed / stalled → "{label} needs attention · Retry"       (deep-link D10)
//
// Scope guard: session + matter only — no persistence across reload, no
// cross-matter (that's the CasesScreen-banner expansion vector). Leak-free:
// polls only while ≥1 tracked intake is non-terminal; a tracked id is dropped
// once toasted or on a 30-min lifetime cap (the sole absence-independent
// leak-guard); the timer tears down when nothing is left to watch. A tracked
// id is NEVER dropped merely for being absent from a poll page (REVIEW
// FINDING 1) — that would lose the toast for an intake pushed off list page-1.
//
// Reuses the F1 endpoint (fetchDiscoveryFiles) — no new backend surface — and
// the adaptive intakePollingCadence keyed on the oldest tracked intake.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastBanner, type Toast } from '../components/Toast';
import { nextPollInterval } from '../lib/intakePollingCadence';
import {
  fetchDiscoveryFiles,
  type DiscoveryDisplayStatus,
  type DiscoveryFileRow,
} from '../lib/database';

export interface TrackedIntake {
  id: string;
  label: string;
  // Wall-clock the track began — a fallback cadence anchor + lifetime cap
  // until the intake first appears in the polled rows.
  trackedAt: number;
  // The intake's own `created_at` (ms epoch), captured the first time it
  // appears in a poll. The adaptive cadence keys on THIS (elapsed since the
  // intake started extracting), not on session track-time — a long
  // extraction must back the cadence off the same way the single-intake
  // loop does. Null until the row is first seen.
  intakeCreatedAtMs: number | null;
}

// Hard lifetime cap: the SOLE leak-guard. Even if a tracked id never reaches
// a terminal pill (a genuinely stuck extraction the health badge surfaces
// separately) OR never appears in a poll (a just-tracked intake that's pushed
// off list page-1 on a busy matter — see REVIEW FINDING 1), stop
// session-watching it after this long so the poll loop is guaranteed to
// drain. 30 min comfortably exceeds the longest expected extraction.
//
// NOTE (REVIEW FINDING 1): we deliberately do NOT drop a tracked id merely
// for being ABSENT from a poll. The watcher polls list page-1 only
// (pageSize:100); on a busy/bulk matter a freshly-tracked intake can sort
// outside page-1 and would, under a miss-count policy, be dropped before it
// terminates — silently breaking F6's "we'll notify you" promise. Absence is
// therefore a no-op (keep watching); the only removal triggers are a terminal
// display_status (toast or silent-drop) and this lifetime cap.
const MAX_TRACK_LIFETIME_MS = 30 * 60 * 1000;

interface IntakeCompletionApi {
  /** Begin watching an intake the user just created this session. Idempotent
   *  per id; a no-op once the id has already been toasted/dropped. */
  trackIntake: (id: string, opts: { label: string }) => void;
}

const IntakeCompletionContext = createContext<IntakeCompletionApi | null>(null);

export function useIntakeCompletion(): IntakeCompletionApi {
  const ctx = useContext(IntakeCompletionContext);
  if (!ctx) {
    // Tolerate absence (e.g. a screen rendered outside CaseShell in a test) —
    // tracking is a best-effort enhancement, never load-bearing.
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return { trackIntake: () => {} };
  }
  return ctx;
}

// Terminal-for-watching display_status values: once a tracked intake reaches
// one of these we either toast (actionable) or drop it (confirmed/cancelled —
// the user finished or abandoned it deliberately, no toast). NOTE these are
// display_status pills, not raw statuses — `needs_review` is the projection of
// raw `ready_for_review`.
const ACTIONABLE: ReadonlySet<DiscoveryDisplayStatus> = new Set<DiscoveryDisplayStatus>([
  'needs_review',
  'failed',
  'stalled',
  'unusable_file',
]);
const SILENT_TERMINAL: ReadonlySet<DiscoveryDisplayStatus> = new Set<DiscoveryDisplayStatus>([
  'confirmed',
  'cancelled',
]);

// Concurrency-safe merge for the watcher's commit. A poll computes `next` from
// a `watching` snapshot taken BEFORE its async fetch; `snapshotIds` is that
// snapshot's id set. The returned updater applies `next` (the snapshot ids'
// updated/kept/dropped state) and PRESERVES any id in `prev` that was NOT in
// the snapshot — i.e. an intake trackIntake()'d while the fetch was in flight.
// Without this, a blind `setTracked(next)` would drop the concurrent add and
// its completion would never toast. Order: preserved concurrent adds first
// (they're newer), then the merged snapshot survivors.
//
// Exported for direct unit testing — the React timing of the clobber race is
// hard to reproduce deterministically through the component, so the merge
// invariant is verified at the function boundary.
export function mergePreservingConcurrentAdds(
  snapshotIds: ReadonlySet<string>,
  next: TrackedIntake[],
): (prev: TrackedIntake[]) => TrackedIntake[] {
  return (prev) => {
    const concurrentAdds = prev.filter((t) => !snapshotIds.has(t.id));
    return [...concurrentAdds, ...next];
  };
}

interface IntakeCompletionProviderProps {
  caseId: string;
  caseKey: string;
  children: ReactNode;
}

export function IntakeCompletionProvider({
  caseId,
  caseKey,
  children,
}: IntakeCompletionProviderProps) {
  const navigate = useNavigate();
  const [tracked, setTracked] = useState<TrackedIntake[]>([]);
  const [toast, setToast] = useState<Toast | null>(null);

  const trackIntake = useCallback((id: string, opts: { label: string }) => {
    setTracked((prev) => {
      if (prev.some((t) => t.id === id)) return prev;
      return [
        ...prev,
        {
          id,
          label: opts.label,
          trackedAt: Date.now(),
          intakeCreatedAtMs: null,
        },
      ];
    });
  }, []);

  // Keep tracked in a ref so the timer effect reads the freshest list without
  // re-arming on each change beyond the count-driven signature below.
  const trackedRef = useRef(tracked);
  trackedRef.current = tracked;

  const workspacePath = `/cases/${caseKey}/discovery/responses`;

  // Build + fire a toast for an actionable transition, then drop the id.
  const fireToast = useCallback(
    (row: DiscoveryFileRow, label: string) => {
      const status = row.display_status;
      if (status === 'needs_review') {
        setToast({
          message: `${label} finished extracting`,
          kind: 'success',
          action: {
            label: 'Review',
            onClick: () => navigate(`${workspacePath}/intake/${row.intake_id}`),
          },
        });
      } else {
        // failed / stalled / unusable_file → "needs attention · Retry".
        // Deep-link honors D10: non-confirmed → /intake/:id (the working flow
        // where Retry / Re-upload live).
        setToast({
          message: `${label} needs attention`,
          kind: 'error',
          action: {
            label: 'Open',
            onClick: () => navigate(`${workspacePath}/intake/${row.intake_id}`),
          },
        });
      }
    },
    [navigate, workspacePath],
  );

  // Signature changes only when the SET of tracked ids changes — so the timer
  // effect re-arms on track/drop, not on every render.
  const trackedSignature = useMemo(
    () => tracked.map((t) => t.id).sort().join('|'),
    [tracked],
  );

  useEffect(() => {
    if (trackedSignature === '') return; // nothing to watch
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    // Drop any tracked id past the lifetime cap. Evaluated INDEPENDENT of the
    // fetch outcome (SHOULD-FIX 1) — a persistently-failing fetch must not
    // bypass the cap and pin the poll loop open forever. Returns the survivors
    // (still within the cap). Pure; no React state writes.
    const pruneExpired = (
      list: TrackedIntake[],
      now: number,
    ): TrackedIntake[] => list.filter((t) => now - t.trackedAt <= MAX_TRACK_LIFETIME_MS);

    const poll = async () => {
      // Snapshot the watch list at poll time, then prune anything past the
      // lifetime cap BEFORE the (failable) fetch — so the cap holds even when
      // the fetch keeps rejecting (SHOULD-FIX 1).
      const now0 = Date.now();
      const watching = pruneExpired(trackedRef.current, now0);
      if (watching.length === 0) {
        // Everything aged out at the top of the tick — commit the drained set
        // so the trackedSignature collapses to '' and the effect tears down.
        const snapshotIds = new Set(trackedRef.current.map((t) => t.id));
        setTracked((prev) => mergePreservingConcurrentAdds(snapshotIds, [])(prev));
        // Re-arm only if a concurrent add survives the merge; otherwise stop.
        scheduleNext(
          mergePreservingConcurrentAdds(snapshotIds, [])(trackedRef.current),
        );
        return;
      }
      let resp;
      try {
        // Fetch a generous page; tracked intakes are few (this session's
        // uploads) and sorted newest-first, so they sit near the top. Using
        // the default branch (no bucket filter) so any terminal state shows.
        resp = await fetchDiscoveryFiles(caseId, { pageSize: 100 });
      } catch {
        // Transient/persistent failure — re-arm against the lifetime-pruned
        // list (NOT the raw snapshot). Once every tracked id ages past the cap
        // the pruned set is empty and scheduleNext stops, so a forever-failing
        // fetch can't poll forever (SHOULD-FIX 1). Commit the prune so the
        // dropped ids leave React state too.
        if (cancelled) return;
        const stillWatching = pruneExpired(trackedRef.current, Date.now());
        const snapshotIds = new Set(trackedRef.current.map((t) => t.id));
        setTracked((prev) =>
          mergePreservingConcurrentAdds(snapshotIds, stillWatching)(prev),
        );
        scheduleNext(
          mergePreservingConcurrentAdds(snapshotIds, stillWatching)(trackedRef.current),
        );
        return;
      }
      if (cancelled) return;

      const byId = new Map(resp.rows.map((r) => [r.intake_id, r]));
      const now = Date.now();
      const next: TrackedIntake[] = [];
      for (const t of watching) {
        // Hard lifetime cap — guarantees the loop drains even for a genuinely
        // stuck intake that never reaches a terminal pill. (Also enforced at
        // the top of the tick + in the catch path, so it holds regardless of
        // fetch outcome.)
        if (now - t.trackedAt > MAX_TRACK_LIFETIME_MS) continue; // drop

        const row = byId.get(t.id);
        if (!row) {
          // Absent from this poll — off-page (busy matter, sorted past
          // page-1), not-yet-visible, or deleted. We do NOT drop on absence
          // (REVIEW FINDING 1): a just-tracked intake that's pushed off
          // page-1 must keep being watched, or its completion toast is lost.
          // The lifetime cap above is the sole leak-guard. Keep watching
          // unchanged (no created_at to capture yet).
          next.push(t);
          continue;
        }

        // Present — capture the intake's own created_at for the cadence
        // (the adaptive-cadence anchor).
        const createdMs = (() => {
          const ms = new Date(row.created_at).getTime();
          return Number.isNaN(ms) ? null : ms;
        })();
        const seen: TrackedIntake = {
          ...t,
          intakeCreatedAtMs: t.intakeCreatedAtMs ?? createdMs,
        };

        if (ACTIONABLE.has(row.display_status)) {
          fireToast(row, t.label);
          continue; // drop — toasted
        }
        if (SILENT_TERMINAL.has(row.display_status)) {
          continue; // drop — user finished/abandoned deliberately, no toast
        }
        // processing → keep watching.
        next.push(seen);
      }

      // Commit via a FUNCTIONAL merge, not a blind `setTracked(next)`
      // replacement. `next` derives from the `watching` snapshot taken before
      // the (async) fetch — if trackIntake() added a NEW id while the fetch
      // was in flight, a blind replace would silently clobber it and its
      // completion would never toast (defeating F6's "we'll notify you"
      // promise). So: apply the snapshot's updates (miss counts / created_at /
      // drops) to the ids that were in `watching`, and PRESERVE any id in
      // `prev` that was added concurrently (not in the snapshot).
      const snapshotIds = new Set(watching.map((t) => t.id));
      const merged = mergePreservingConcurrentAdds(snapshotIds, next);
      setTracked((prev) => merged(prev));

      // Schedule against the post-merge set so a concurrently-added id is
      // actually polled. We can't read the merged result synchronously from
      // `prev` here (the updater runs later), so recompute the same merge
      // against the current ref — which reflects any concurrent add.
      scheduleNext(merged(trackedRef.current));
    };

    const scheduleNext = (watching: TrackedIntake[]) => {
      if (cancelled) return;
      if (watching.length === 0) return; // all settled — stop (leak-free)
      // Adaptive cadence keyed on the OLDEST tracked intake's elapsed time
      // SINCE THE INTAKE STARTED (its created_at) once known, falling back to
      // session track-time only for intakes not yet seen in a poll.
      const elapsedMs = watching.reduce((max, t) => {
        const anchor = t.intakeCreatedAtMs ?? t.trackedAt;
        return Math.max(max, Date.now() - anchor);
      }, 0);
      timer = setTimeout(poll, nextPollInterval(Math.max(0, elapsedMs)));
    };

    // First poll on a short delay so a just-created intake has a beat to
    // appear, then the cadence takes over.
    timer = setTimeout(poll, 2_000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedSignature, caseId, fireToast]);

  // Auto-dismiss the toast (matches the workspace toast cadence).
  useEffect(() => {
    if (!toast) return;
    const ms = toast.kind === 'error' ? 8000 : 6000;
    const t = setTimeout(() => setToast(null), ms);
    return () => clearTimeout(t);
  }, [toast]);

  const api = useMemo<IntakeCompletionApi>(() => ({ trackIntake }), [trackIntake]);

  return (
    <IntakeCompletionContext.Provider value={api}>
      {children}
      {toast && <ToastBanner toast={toast} onDismiss={() => setToast(null)} />}
    </IntakeCompletionContext.Provider>
  );
}
